import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  DollarSign, 
  TrendingUp, 
  Calendar, 
  Award,
  FileText,
  Download,
  Eye,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBonus } from '../contexts/BonusContext';
import { Progress } from './ui/progress';

export function PayrollPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getBonusesByEmployee, getTotalBonusForEmployee } = useBonus();
  
  const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const employeeBonuses = user ? getBonusesByEmployee(user.id) : [];
  const currentMonthBonuses = employeeBonuses.filter(b => b.month === currentMonth);
  const totalCurrentMonth = user ? getTotalBonusForEmployee(user.id, currentMonth) : 0;
  const totalAllTime = user ? getTotalBonusForEmployee(user.id) : 0;

  // Agrupar bonificaciones por mes
  const bonusesByMonth = employeeBonuses.reduce((acc, bonus) => {
    if (!acc[bonus.month]) {
      acc[bonus.month] = [];
    }
    acc[bonus.month].push(bonus);
    return acc;
  }, {} as Record<string, typeof employeeBonuses>);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('es-MX', { year: 'numeric', month: 'long' });
  };

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl mb-2">Nómina y Bonificaciones</h1>
        <p className="text-muted-foreground">
          Consulta tus bonificaciones por workflows aprobados
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Bonificaciones Este Mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {formatCurrency(totalCurrentMonth)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {currentMonthBonuses.length} bonificación(es)
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total Acumulado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {formatCurrency(totalAllTime)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Desde el inicio
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-purple-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Award className="h-4 w-4" />
              Workflows Bonificados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{employeeBonuses.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Total de workflows
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-orange-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Promedio Mensual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {formatCurrency(totalAllTime / Math.max(Object.keys(bonusesByMonth).length, 1))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Por mes trabajado
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bonificaciones por mes */}
      <Tabs defaultValue="current" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="current">Mes Actual</TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
          <TabsTrigger value="all">Todas</TabsTrigger>
        </TabsList>

        {/* Mes actual */}
        <TabsContent value="current" className="space-y-4 mt-6">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
            <CardHeader>
              <CardTitle>Bonificaciones de {formatMonth(currentMonth)}</CardTitle>
              <CardDescription>
                Bonificaciones que se reflejarán en tu nómina de este mes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {currentMonthBonuses.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay bonificaciones en este mes todavía</p>
                  <p className="text-sm mt-2">¡Sigue creando workflows de calidad para ganar bonificaciones!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {currentMonthBonuses.map((bonus) => (
                    <Card key={bonus.id} className="border-border">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                                <Award className="h-5 w-5 text-green-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold">{bonus.workflowName}</h3>
                                <p className="text-sm text-muted-foreground">{bonus.reason}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground ml-13">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(bonus.awardedAt).toLocaleDateString('es-MX')}
                              </div>
                              <div className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {bonus.voucherNumber}
                              </div>
                              <Badge variant={bonus.status === 'paid' ? 'default' : 'secondary'}>
                                {bonus.status === 'paid' ? (
                                  <>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Pagado
                                  </>
                                ) : (
                                  <>
                                    <Clock className="h-3 w-3 mr-1" />
                                    Pendiente
                                  </>
                                )}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-600">
                                {formatCurrency(bonus.amount)}
                              </p>
                              <p className="text-xs text-muted-foreground">USD</p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/bonus/${bonus.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Ver Comprobante
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Resumen del mes */}
                  <Card className="border-2 border-green-500/20 bg-green-500/5">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Total a recibir en nómina de {formatMonth(currentMonth)}</p>
                          <p className="text-3xl font-bold text-green-600 mt-1">
                            {formatCurrency(totalCurrentMonth)}
                          </p>
                        </div>
                        <Button className="bg-green-600 hover:bg-green-700">
                          <Download className="mr-2 h-4 w-4" />
                          Descargar Resumen
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Historial por mes */}
        <TabsContent value="history" className="space-y-4 mt-6">
          <ScrollArea className="h-[600px]">
            <div className="space-y-4 pr-4">
              {Object.keys(bonusesByMonth)
                .sort()
                .reverse()
                .map((month) => {
                  const monthBonuses = bonusesByMonth[month];
                  const monthTotal = monthBonuses.reduce((sum, b) => sum + b.amount, 0);
                  
                  return (
                    <Card key={month} className="relative overflow-hidden border-border">
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg">{formatMonth(month)}</CardTitle>
                            <CardDescription>{monthBonuses.length} bonificación(es)</CardDescription>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">
                              {formatCurrency(monthTotal)}
                            </p>
                            <p className="text-xs text-muted-foreground">Total del mes</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {monthBonuses.map((bonus) => (
                          <div
                            key={bonus.id}
                            className="flex items-center justify-between p-3 bg-accent/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <Award className="h-5 w-5 text-primary" />
                              <div>
                                <p className="font-medium text-sm">{bonus.workflowName}</p>
                                <p className="text-xs text-muted-foreground">{bonus.voucherNumber}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="font-bold text-green-600">{formatCurrency(bonus.amount)}</p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/bonus/${bonus.id}`)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Todas las bonificaciones */}
        <TabsContent value="all" className="space-y-4 mt-6">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
            <CardHeader>
              <CardTitle>Todas tus Bonificaciones</CardTitle>
              <CardDescription>Historial completo de bonificaciones recibidas</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2 pr-4">
                  {employeeBonuses.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Aún no tienes bonificaciones</p>
                    </div>
                  ) : (
                    employeeBonuses.map((bonus) => (
                      <Card key={bonus.id} className="border-border">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Award className="h-5 w-5 text-primary" />
                              <div>
                                <p className="font-medium text-sm">{bonus.workflowName}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(bonus.awardedAt).toLocaleDateString('es-MX')}
                                  </p>
                                  <span className="text-xs text-muted-foreground">•</span>
                                  <p className="text-xs text-muted-foreground">{bonus.voucherNumber}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant={bonus.status === 'paid' ? 'default' : 'secondary'}>
                                {bonus.status === 'paid' ? 'Pagado' : 'Pendiente'}
                              </Badge>
                              <p className="font-bold text-green-600 min-w-[100px] text-right">
                                {formatCurrency(bonus.amount)}
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/bonus/${bonus.id}`)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info adicional */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Cómo Ganar Bonificaciones
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Crea workflows de alta calidad que resuelvan problemas reales del holdings</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Prueba exhaustivamente tus workflows antes de enviarlos a revisión</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Cuando tu workflow sea aprobado, recibirás automáticamente una bonificación</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Las bonificaciones se reflejan en la nómina del mes correspondiente</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Puedes descargar comprobantes para tus registros personales y fiscales</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
